import { Fragment, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@mui/material'
import * as S from './MultiProductHomePage.styles'
import { photosTheme as theme } from 'mixtiles-web-common'
import { PlaybackProvider } from 'components/PlaybackProvider/PlaybackContext'
import BrowserThemeColor from 'components/ThemeColor/BrowserThemeColor'
import TopBar from 'elements/TopBar/TopBar'
import TopBarBanner from 'elements/TopBar/TopBarBanner/TopBarBanner'
import FooterSection from 'components/FooterSection/FooterSection'
import {
  AI_GENERATION_URL,
  PHOTO_BOOK_HOMEPAGE_URL,
  PHOTO_BOOK_URL,
  PHOTO_STYLER_HOMEPAGE_URL,
  PHOTO_STYLER_URL,
} from 'config/config'
import {
  usePhotoBookInProgress,
  useTilesInProgress,
} from 'hooks/useWorkInProgress'
import useScreenSize from 'hooks/useScreenSize'
import { useUserDeviceData } from 'services/UserDeviceContext'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { genericBannerManager } from 'services/GenericBannerManager'
import ProductHeroSection from './Components/ProductHeroSection/ProductHeroSection'
import PetPortraitHeroSection from './Components/PetPortraitHeroSection/PetPortraitHeroSection'
import NewProductsGrid from './Components/NewProductsGrid/NewProductsGrid'
import AppSection from './Components/AppSection/AppSection'
import DesktopAppSection from './Components/DesktopAppSection/DesktopAppSection'
import BestPlaceSection from './Components/BestPlaceSection/BestPlaceSection'
import HoverPlayback from './Components/HoverPlayback/HoverPlayback'
import MostlyVisiblePlayback from './Components/MostlyVisiblePlayback/MostlyVisiblePlayback'
import {
  APP_STORE_URL,
  type CtaLink,
  DESKTOP_MULTI_PRODUCT_STATS,
  type HeroPlayback,
  MULTI_PRODUCT_HERO_VIDEOS,
  MULTI_PRODUCT_STATS,
  MULTI_PRODUCT_PHOTO_WALLS_PHOTOSHOOT_MOBILE_VIDEO,
} from './multiProductHomePage.consts'
import useMultiProductHomePage from './useMultiProductHomePage'
import { buildHeroCtas } from './buildHeroCtas'

type SideBySideHero = 'photo-books' | 'pet-portraits'

export default function MultiProductHomePage() {
  const { t, i18n } = useTranslation()
  const { onCtaClick, newProductItems, desktopNewProductItems } =
    useMultiProductHomePage()
  const experimentManager = useExperimentManager()
  const isPhotoBooksEnabled = experimentManager.isEnabled('photo-books')
  const hasTilesInProgress = useTilesInProgress()
  const hasPhotoBookInProgress = usePhotoBookInProgress()
  const { isIOS } = useUserDeviceData()
  const isMediumScreen = useScreenSize('md')
  // The server can't tell a mouse from a touch screen, so it renders for a mouse.
  const isHoverDevice = useMediaQuery('(hover: hover)', {
    defaultMatches: true,
  })
  // The section only offers the App Store app, so Android phones get no app section at all.
  const shouldShowAppSection = isIOS || isMediumScreen
  const heroVideoScreen = isMediumScreen ? 'desktop' : 'mobile'
  // Gate the experiment exposure on the phone layout, the only one that shows this video.
  const isPhotoshootTilesHeroVideoEnabled =
    !isMediumScreen &&
    experimentManager.isEnabled('multi-product-tiles-hero-video')
  const photoWallsMobileHeroVideo = isPhotoshootTilesHeroVideoEnabled
    ? MULTI_PRODUCT_PHOTO_WALLS_PHOTOSHOOT_MOBILE_VIDEO
    : MULTI_PRODUCT_HERO_VIDEOS.photoWalls.mobile
  const photoWallsHeroVideo = isMediumScreen
    ? MULTI_PRODUCT_HERO_VIDEOS.photoWalls.desktop
    : photoWallsMobileHeroVideo
  const photoBooksHeroVideo =
    MULTI_PRODUCT_HERO_VIDEOS.photoBooks[heroVideoScreen]
  // Side by side, Photo Books and Pet Portraits are equally visible, so the page's single
  // player would flip between them. Each gets its own: with a mouse it plays on hover, on a
  // touch screen while it's on screen and its turn.
  // Without Photo Books, Pet Portraits spans the row alone and plays like Photo Walls.
  // With no hero below it in the page's player to take over, Photo Walls pauses itself once
  // it's mostly scrolled past, as it does on mobile when the next hero takes over.
  const areHeroesSideBySide = isMediumScreen && isPhotoBooksEnabled
  const SeparateHeroPlayback = isHoverDevice ? HoverPlayback : PlaybackProvider
  const SideBySideHeroPlayback = areHeroesSideBySide
    ? SeparateHeroPlayback
    : Fragment
  const areSideBySideHeroesTakingTurns = areHeroesSideBySide && !isHoverDevice
  const [sideBySideHeroOnTurn, setSideBySideHeroOnTurn] =
    useState<SideBySideHero>('photo-books')
  const photoWallsRef = useRef<HTMLDivElement>(null)

  const getSideBySideHeroPlayback = (hero: SideBySideHero): HeroPlayback => {
    if (!areSideBySideHeroesTakingTurns) return 'loop'
    return hero === sideBySideHeroOnTurn ? 'play-once' : 'wait'
  }

  const heroCtaLabels = {
    learnMore: t('general.learn_more'),
    shopNow: t('multi_product_homepage.shop_now'),
    continueDesigning: t('multi_product_homepage.continue_designing'),
  }

  const photoWallsHero = (
    <ProductHeroSection
      key={photoWallsHeroVideo.cldId}
      ref={photoWallsRef}
      testId="hero-photo-walls"
      video={photoWallsHeroVideo}
      playback="loop"
      videoName="Photo Walls Intro"
      title={t('multi_product_homepage.photo_walls_title')}
      subtitle={t('multi_product_homepage.photo_walls_subtitle')}
      ctas={buildHeroCtas({
        product: 'Single Tiles',
        learnMoreUrl: PHOTO_STYLER_HOMEPAGE_URL,
        buyUrl: PHOTO_STYLER_URL,
        isWorkInProgress: hasTilesInProgress,
        labels: heroCtaLabels,
        onCtaClick,
      })}
    />
  )

  const appDownload: CtaLink = {
    label: t('multi_product_homepage.download'),
    to: APP_STORE_URL,
    onClick: (e) =>
      onCtaClick(e, {
        url: APP_STORE_URL,
        action: 'Download',
        product: 'App',
      }),
  }

  const stats = useMemo(() => {
    const shownStats = isMediumScreen
      ? DESKTOP_MULTI_PRODUCT_STATS
      : MULTI_PRODUCT_STATS
    return shownStats.map(({ value, suffix, captionKey, notation }) => {
      const formattedValue = new Intl.NumberFormat(i18n.language, {
        notation,
      }).format(value)
      return { value: `${formattedValue}${suffix}`, caption: t(captionKey) }
    })
  }, [i18n.language, t, isMediumScreen])

  return (
    <S.Container className="MultiProductHomePage">
      <BrowserThemeColor color={theme.colors.white} />
      <TopBar
        noBorder
        backgroundColor="transparent"
        iconsColor={theme.colors.black}
        source="Multi Product Homepage"
      >
        <TopBar.BottomContent>
          <TopBarBanner bannerManagerInstance={genericBannerManager} />
        </TopBar.BottomContent>
      </TopBar>
      <PlaybackProvider>
        <S.VideosContainer>
          {areHeroesSideBySide ? (
            <MostlyVisiblePlayback targetRef={photoWallsRef}>
              {photoWallsHero}
            </MostlyVisiblePlayback>
          ) : (
            photoWallsHero
          )}
          {isPhotoBooksEnabled && (
            <SideBySideHeroPlayback>
              <ProductHeroSection
                key={photoBooksHeroVideo.cldId}
                testId="hero-photo-books"
                video={photoBooksHeroVideo}
                playback={getSideBySideHeroPlayback('photo-books')}
                onPlayedOnce={() => setSideBySideHeroOnTurn('pet-portraits')}
                videoName="Photo Books Intro"
                title={t('product_selection.photo_books_name')}
                subtitle={t('multi_product_homepage.photo_books_subtitle')}
                ctas={buildHeroCtas({
                  product: 'Photo Books',
                  learnMoreUrl: PHOTO_BOOK_HOMEPAGE_URL,
                  buyUrl: PHOTO_BOOK_URL,
                  isWorkInProgress: hasPhotoBookInProgress,
                  labels: heroCtaLabels,
                  onCtaClick,
                })}
              />
            </SideBySideHeroPlayback>
          )}
          <SideBySideHeroPlayback>
            <PetPortraitHeroSection
              testId="hero-pet-portraits"
              introVideo={
                MULTI_PRODUCT_HERO_VIDEOS.petPortraitsIntro[heroVideoScreen]
              }
              loopVideo={
                MULTI_PRODUCT_HERO_VIDEOS.petPortraitsLoop[heroVideoScreen]
              }
              playback={getSideBySideHeroPlayback('pet-portraits')}
              onPlayedOnce={() => setSideBySideHeroOnTurn('photo-books')}
              title={t('ai_templates.menu_title_pet')}
              subtitle={t('multi_product_homepage.pet_portraits_subtitle')}
              explore={{
                label: t('multi_product_homepage.explore_now'),
                to: AI_GENERATION_URL,
                onClick: (e) =>
                  onCtaClick(e, {
                    url: AI_GENERATION_URL,
                    action: 'Explore',
                    product: 'Pet Portraits',
                    requireQuiz: true,
                  }),
              }}
            />
          </SideBySideHeroPlayback>
        </S.VideosContainer>
        <NewProductsGrid
          title={
            isMediumScreen
              ? t('multi_product_homepage.desktop_new_products_title')
              : t('multi_product_homepage.new_products_title')
          }
          items={isMediumScreen ? desktopNewProductItems : newProductItems}
        />
        {shouldShowAppSection &&
          (isMediumScreen ? (
            <DesktopAppSection
              // The QR code hands the install over from a computer to a phone. Tablets and
              // landscape phones are as wide but can't hover, and install from the link.
              shouldShowQrCode={isHoverDevice}
              title={t('multi_product_homepage.app_title')}
              subtitle={t('multi_product_homepage.app_subtitle')}
              download={appDownload}
              appStoreBadgeLabel={t('multi_product_homepage.app_store_badge')}
            />
          ) : (
            <AppSection
              title={t('multi_product_homepage.app_title')}
              subtitle={t('multi_product_homepage.app_subtitle')}
              download={appDownload}
            />
          ))}
        <BestPlaceSection
          title={t('multi_product_homepage.best_place_title')}
          stats={stats}
        />
        <S.FooterContainer>
          <FooterSection showEmailSignup={false} />
        </S.FooterContainer>
      </PlaybackProvider>
    </S.Container>
  )
}
